import type { Deal, DealSection } from './types';

/**
 * What pressing a star does to a deal's section.
 *
 * Kyu, 10 September: "Every time I click any deals in CG or partnership as
 * starred, they should automatically move to the starred section. And once I
 * unclick them, they go back to wherever they are before I starred them."
 *
 * Pure, and deliberately the ONLY place that decides this. The star is
 * pressed from three screens now (the List row, the drawer, the All Deals
 * table) and three copies of "and also move it" would drift.
 *
 * The flag stays the source of truth for what is starred. The section move
 * is a consequence of it. Nothing reads the section to answer "is this
 * starred", which is what stops the two from ever disagreeing.
 */

export interface StarChanges {
  in_ceo_brief: boolean;
  section_id?: string | null;
  section_before_star?: string | null;
}

/**
 * The section that collects starred deals for this pipeline, or null.
 *
 * Found by the holds_starred flag rather than by the name, so renaming the
 * section does not silently stop the move. A pipeline with no such section
 * is not an error: the star still flags, All Deals still collects, and the
 * deal simply stays where it is.
 */
export function starredSectionFor(
  sections: readonly DealSection[],
  pipeline: Deal['pipeline'],
): DealSection | null {
  return sections.find((section) => section.pipeline === pipeline && section.holds_starred) ?? null;
}

export function starChanges(deal: Deal, sections: readonly DealSection[]): StarChanges {
  const starring = !deal.in_ceo_brief;
  const target = starredSectionFor(sections, deal.pipeline);

  if (target === null) {
    // No Starred section on this pipeline. Flag only, and do NOT stash a
    // section we are not moving away from: a stash written now would be
    // restored later over a section the deal never left.
    return { in_ceo_brief: starring };
  }

  if (starring) {
    // Already sitting in the Starred section, starred by hand-dragging
    // rather than by the star. Nothing to remember, nowhere to move.
    if (deal.section_id === target.id) {
      return { in_ceo_brief: true };
    }
    return {
      in_ceo_brief: true,
      section_id: target.id,
      section_before_star: deal.section_id,
    };
  }

  // Unstarring. Put it back where it came from and forget, so a stale answer
  // cannot be used a second time.
  //
  // Only when it is actually still in the Starred section: if somebody has
  // since dragged it somewhere else by hand, that drag is the more recent
  // statement of where it belongs and unstarring must not undo it.
  if (deal.section_id !== target.id) {
    return { in_ceo_brief: false, section_before_star: null };
  }
  return {
    in_ceo_brief: false,
    section_id: deal.section_before_star,
    section_before_star: null,
  };
}
